import axios, { isAxiosError } from 'axios'
import { defineStore } from 'pinia'
import { BASE_URL } from '@/environment/environment'
import type { IApiStoreState } from '@/modules/apiStore/types/IApiStoreState'
import type { ITreeNode } from '@/modules/apiStore/types/ITreeNode'
import type { IDashboardStats } from '@/modules/apiStore/types/adverse-events-stats/IDashboardStats'
import type { IHistoryLog } from '@/types/IHistoryLog'
import type { IStats } from '@/modules/apiStore/types/adverse-events-stats/IStats'
import type { IAdverseEvent } from '@/modules/apiStore/types/adverse-events/IAdverseEvent'
import type { ICreateAdversePayload } from '@/modules/apiStore/types/adverse-events/ICreateAdversePayload'
import type { IProbabilityConsequenceResponse } from '@/modules/apiStore/types/adverse-events/IProbabilityConsequenceResponse'
import type { ISelectOption } from '@/modules/apiStore/types/adverse-events/ISelectOption'
import type { ICurrentEmployee } from '@/modules/apiStore/types/employees/ICurrentEmployee'
import type { IFilters } from '@/modules/apiStore/types/filters/IFilters'
import type { IHospitalSkeleton } from '@/modules/apiStore/types/hospital-skeleton/IHospitalSkeleton'
import type { IRealtimePayload } from '@/modules/centrifuge/types/IRealtimePayload'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import type { IResponsibilityEntry } from '@/modules/tasks/types/IResponsibilityEntry'
import type { IFilterChoiceItem } from '@/modules/universalStats/types/IFilterChoiceItem'
import type { ICustomAxiosRequestConfig } from '@/types/ICustomAxiosRequestConfig'
import type { IUniversalTreeSelectModule } from '@/types/IUniversalTreeSelectModule'
import { logger } from '@/utils/eventLogger'
import { toApiDate } from '@/utils/formatters'
import { isHttpNotFound } from '@/utils/httpErrors'
import { adverseReportFilters } from '@/modules/adverseEvents/utils/reportFilters'
import { currentAuthEpoch } from '@/utils/authRecovery'
import { onboardingEntityApiUrl } from '@/modules/onboarding/utils/demoApi'
/** Поля оценки риска, которые бэкенд может пересчитать в PATCH-ответе */
interface IAdverseEventRiskFields {
  probability?: number | null
  consequence?: number | null
  risk?: string
  risk_color?: string
}

/*
 * Точечно переносим оценку риска (и пересчитанный risk), не затирая
 * остальной current/editable — иначе автосейв вкладки координатора
 * сотрёт несохранённые правки вкладки «Форма».
 */
function applyAdverseEventRiskFields(
  target: IAdverseEventRiskFields | null,
  source: IAdverseEventRiskFields,
) {
  if (!target) return
  if (source.probability !== undefined) target.probability = source.probability
  if (source.consequence !== undefined) target.consequence = source.consequence
  if (source.risk !== undefined) target.risk = source.risk
  if (source.risk_color !== undefined) target.risk_color = source.risk_color
}

const inFlightAdverseEventById = new Map<number, Promise<void>>()
const inFlightAdverseEventLogs = new Map<number, Promise<IHistoryLog[]>>()
let realtimeRefreshTimer: ReturnType<typeof setTimeout> | null = null

export const useApiStore = defineStore('apiStore', {
  state: (): IApiStoreState => ({
    adverseEventsRevision: 0,
    adverseEventsRequestRevision: 0,
    adverseSearchError: '',
    externalAdverseEventId: null,
    adverseEventLogs: [] as IHistoryLog[],
    nextAdverseEventsCursor: null,
    departments: [],
    adverseEvents: [],
    totalAdverseEventsCount: 0,
    itemsPerPage: 25,
    adverseEventsStats: {} as IStats,
    statsDashboard: {} as IDashboardStats,
    employees: [],
    hospitalSkeleton: [] as IHospitalSkeleton[],
    categoryTypeTree: [],
    flatDepartments: [],
    hospitalTree: [],
    departmentsTree: [],
    isShowCoordinatorReport: false,
    adverseEventProbability: [],
    adverseEventConsequences: [],
    riskLevels: [],
    filterChoices: {},
    searchChoices: {},
    filterCursors: {},
    filterLoading: {},
    filterStrategies: {},
    treeChoices: {},
    treeSearchChoices: {},
    treeCursors: {},
    treeLoading: {},
    participantTypeOptions: [
      { value: 'employee', label: 'Сотрудник' },
      { value: 'patient', label: 'Пациент' },
      { value: 'visitor', label: 'Посетитель' },
    ],
    filters: {
      categories: [],
      coordinators: [],
      creators: [],
      created_after: null,
      created_before: null,
      cursor: null,
      departments: [],
      event_types: [],
      is_active: null,
      requires_action: null,
      is_actual: null,
      numbers: [],
      risk: [],
      search: '',
      status: [],
    } as IFilters,
    currentSortString: null,
    currentAdverseEvent: {
      event_type: { id: '' },
      location: null,
      location_str: '',
      department: null,
      description: '',
      corrective_measures: '',
      status: 'new',
      probability: undefined,
      consequence: undefined,
      date_time: '',
      participants: [],
      coordinator_entry: null,
    } as ICreateAdversePayload,
    editableAdverseEvent: null,
  }),
  getters: {
    hasActiveFilters(state): boolean {
      const f = state.filters
      if (!f) return false
      if (f.search && f.search.trim() !== '') return true
      if (f.advanced) return true
      if (f.created_after !== null || f.created_before !== null) return true
      if (f.is_active !== null && f.is_active !== undefined) return true
      if (f.requires_action !== null && f.requires_action !== undefined) return true
      if (Array.isArray(f.categories) && f.categories.length > 0) return true
      if (Array.isArray(f.coordinators) && f.coordinators.length > 0) return true
      if (Array.isArray(f.creators) && f.creators.length > 0) return true
      if (Array.isArray(f.departments) && f.departments.length > 0) return true
      if (Array.isArray(f.event_types) && f.event_types.length > 0) return true
      if (Array.isArray(f.numbers) && f.numbers.length > 0) return true
      if (Array.isArray(f.risk) && f.risk.length > 0) return true
      if (Array.isArray(f.status) && f.status.length > 0) return true
      return false
    },
    /**
     * Геттер для преобразования отделений в древовидную структуру
     *
     * Назначение:
     * - Формирует иерархическую структуру для отображения в Tree-компонентах
     * - Блоки становятся родительскими узлами, отделения - дочерними
     *
     * Возвращает:
     * - Массив объектов ITreeNode с вложенной структурой
     *
     * Структура узла:
     * {
     *   key: string - уникальный идентификатор узла
     *   label: string - отображаемое название
     *   data: {
     *     name: string - название элемента
     *     position: number - позиция (по умолчанию 0)
     *     selfId: string - оригинальный ID из базы
     *   },
     *   icon: string - иконка узла
     *   selectable: boolean - можно ли выбрать узел
     *   children: ITreeNode[] - дочерние узлы
     * }
     *
     * Особенности:
     * - Ключи блоков формируются как "block-{blockId}"
     * - Ключи отделений формируются как "dept-{departmentId}"
     * - Родительские узлы (блоки) неселектабельны
     */
    departmentTree(): ITreeNode[] {
      const blocksMap = new Map<string, ITreeNode>()

      this.departments.forEach(dept => {
        // Проверяем что dept существует и имеет необходимые поля
        if (!dept || !dept.id || !dept.name) {
          return
        }

        const blockId = dept.block?.id || 'no-block'
        const blockName = dept.block?.name?.trim() || 'Без блока'
        const deptName = dept.name?.trim() || 'Без названия'

        if (!blocksMap.has(blockId)) {
          blocksMap.set(blockId, {
            key: `block-${blockId}`,
            label: blockName,
            data: {
              name: blockName,
              position: 0,
              selfId: blockId,
            },
            children: [],
          })
        }

        const deptNode: ITreeNode = {
          key: `dept-${dept.id}`,
          label: deptName,
          data: {
            name: deptName,
            position: 0,
            selfId: dept.id,
          },
          children: [],
        }

        blocksMap.get(blockId)?.children.push(deptNode)
      })

      return Array.from(blocksMap.values())
    },

    /**
     * Получает название типа события
     *
     * Возвращает имя типа события или 'Без уточнения',
     * если тип не указан или равен null
     *
     * Используется для отображения читаемых названий типов событий
     * в интерфейсе вместо null/undefined значений
     */
    getEventTypeName: () => (eventType: { id: number; name?: string } | null): string => {
      return eventType?.name ?? 'Без уточнения'
    },

    /**
     * Возвращает последний статус ответственности без изменений
     *
     * Функция-заглушка, которая просто возвращает переданный статус
     * Может использоваться как основа для будущей логики обработки статусов
     *
     * В текущей реализации не выполняет преобразований,
     * просто возвращает исходное значение
     */
    getLastResponsibilityStatus: () => (status: string): string => {
      return status
    },

    /**
     * Маппинг статусов мероприятий на читаемые названия
     *
     * Используется для отображения статусов в интерфейсе
     * вместо технических значений. Содержит все возможные
     * статусы мероприятий по устранению нежелательных событий.
     *
     * Соответствие статусов:
     * - new → 'Новое'
     * - passed → 'Передано'
     * - in_progress → 'В работе'
     * - recalled → 'Отозвано'
     * - returned → 'Возвращено'
     * - rejected → 'Отклонено'
     * - cancelled → 'Отменено'
     * - completed → 'Завершено'
     */
    responsibilityEntriesStatuses: () => ({
      new: 'Новое',
      passed: 'Передано',
      in_progress: 'В работе',
      recalled: 'Отозвано',
      returned: 'Возвращено',
      rejected: 'Отклонено',
      cancelled: 'Отменено',
      completed: 'Завершено',
      waiting: 'Ожидает',
    }),
  },

  actions: {

    /**
     * Загрузка классификатора типов нежелательных событий
     * @param fieldKey - Ключ поля в сторе (например, 'event_type')
     * @param search - Строка поиска
     * @param cursor - Курсор для пагинации (next из ответа API)
     */
    async fetchTreeChoices(fieldKey: string, search?: string, cursor?: string): Promise<void> {
      this.treeLoading[fieldKey] = true

      try {
        const response = await axios.get(`${BASE_URL}/api/adverse/adverse-event-type/`, {
          params: {
            search: search || undefined,
            cursor: cursor || undefined,
          },
        })

        const mapTree = (items: any[]): IFilterChoiceItem[] => {
          return items.map(item => ({
            id: item.id,
            label: item.name,
            children: item.children && item.children.length > 0 ? mapTree(item.children) : [],
          }))
        }

        const formattedData = mapTree(response.data.results)

        if (cursor) {
          this.treeChoices[fieldKey] = [...(this.treeChoices[fieldKey] || []), ...formattedData]
        } else if (search) {
          this.treeSearchChoices[fieldKey] = formattedData
        } else {
          this.treeChoices[fieldKey] = formattedData
        }

        this.treeCursors[fieldKey] = response.data.next
      } finally {
        this.treeLoading[fieldKey] = false
      }
    },

    /**
     * Универсальный метод для преобразования массива данных
     * в древовидную структуру `ITreeNode[]`.
     *
     * Используется в случаях, когда у объектов есть иерархия через поле `children`,
     * и требуется построить дерево для отображения или выбора.
     *
     * Каждый элемент входного массива должен содержать:
     * - `id` — уникальный идентификатор
     * - `name` — отображаемое название
     * - `children` (опционально) — вложенный массив элементов той же структуры
     *
     * @param {any[]} data — Исходный массив объектов, которые нужно преобразовать в дерево.
     * Если пустой или не передан, вернётся пустой массив.
     *
     * @returns {ITreeNode[]} Готовая древовидная структура, где каждый узел содержит:
     * - `key` — уникальный ключ узла (равен `id`)
     * - `label` — метка для отображения (равна `name`)
     * - `data` — объект с дополнительными данными (`name`, `selfId`)
     * - `selectable` — признак выбираемости ноды
     * - `children` — вложенные узлы, построенные по той же логике
     */
    getTree(data: any[]): ITreeNode[] {
      if (!data || data.length === 0) return []

      const buildTree = (data: any[]): ITreeNode[] => {
        return data.map(data => ({
          key: data.id,
          label: data.name,
          data: {
            name: data.location_type_name,
            selfId: data.id,
          },
          selectable: true,
          children: buildTree(data.children || []),
        }))
      }

      return buildTree(data)
    },

    /** Invalidates the server-filtered list and safely refreshes an open card.
     * Voice responses still populate only the matching transcription request.
     */
    async applyAdverseEventRealtime(payload: IRealtimePayload | any): Promise<void> {
      // Экземпляр стора для работы с UI-уведомлениями
      const feedbackStore = useFeedbackStore()

      // Прерываем выполнение, если структура payload некорректна
      if (!payload?.event_type || !payload?.data) return

      // Обработка специального события расшифровки голоса
      if (payload.event_type === 'voice_template') {
        /*
         * Фильтр чужих/устаревших ответов: бек возвращает тот же request_id,
         * что фронт отправил вместе с voice_file. Если не совпадает —
         * это либо чужой пользователь, либо запрос, отменённый по таймауту.
         */
        if (
          !payload.request_id
          || payload.request_id !== feedbackStore.transcriptionRequestId
        ) return

        feedbackStore.isAwaitTranscription = false

        if (feedbackStore.transcriptionTimeout) {
          clearTimeout(feedbackStore.transcriptionTimeout)
          feedbackStore.transcriptionTimeout = null
        }

        // Данные, полученные от сервера транскрибации
        const voiceData = payload.data

        // Флаг, показывающий, были ли применены изменения к форме
        let hasUpdates = false

        // Проходим по каждому полю пришедших данных
        for (const key in voiceData) {
          // Если значение поля актуально (не null)
          if (voiceData[key] !== null) {
            // Подставляем значение в текущее заполняемое НС
            // @ts-expect-error
            this.currentAdverseEvent[key] = voiceData[key]
            // Отмечаем, что изменения есть
            hasUpdates = true
          }
        }

        // Если форма была обновлена, уведомляем пользователя
        if (hasUpdates) {
          feedbackStore.showToast({
            type: 'success',
            message: 'Голосовое сообщение успешно расшифровано и прикреплено к форме',
            time: 5000,
          })
        }

        // Ответ применён — обнуляем id, чтобы повторный приход того же события не сработал.
        feedbackStore.transcriptionRequestId = null

        // Прерываем выполнение, так как это не крудовская операция с массивом событий
        return
      }

      if (!['adverse_event_created', 'adverse_event_updated', 'adverse_event_deleted'].includes(payload.event_type)) return
      const ev = payload.data as IAdverseEvent

      if (this.hasActiveFilters) {
        await this.fetchFilteredAdverseEvents(undefined, undefined, false)
      } else {
        const idx = this.adverseEvents.findIndex(e => e.id === ev.id)
        switch (payload.event_type) {
          case 'adverse_event_created':
            if (idx >= 0) this.adverseEvents.splice(idx, 1)
            this.adverseEvents = [ev, ...this.adverseEvents]
            this.totalAdverseEventsCount++
            feedbackStore.showToast({ type: 'info', message: `Получено новое НС № ${ev.number}`, time: 5000 })
            break
          case 'adverse_event_updated':
            if (idx >= 0) {
              this.adverseEvents.splice(idx, 1, ev)
            } else {
              this.adverseEvents = [ev, ...this.adverseEvents]
            }
            break
          case 'adverse_event_deleted':
            if (idx >= 0) {
              this.adverseEvents.splice(idx, 1)
              this.totalAdverseEventsCount = Math.max(0, this.totalAdverseEventsCount - 1)
              feedbackStore.showToast({ type: 'warning', message: `НС № ${ev.number} удалено`, time: 5000 })
            }
            break
        }
      }

      if (this.currentAdverseEvent?.id !== ev.id) return
      if (payload.event_type === 'adverse_event_deleted') {
        feedbackStore.showToast({ type: 'warning', message: 'Открытое НС удалено другим сотрудником', time: 7000 })
        return
      }
      if (realtimeRefreshTimer) {
        clearTimeout(realtimeRefreshTimer)
      }
      realtimeRefreshTimer = setTimeout(async () => {
        realtimeRefreshTimer = null
        await this.refreshAdverseEventDetailsRealtime(ev.id)
      }, 150)
    },

    async refreshAdverseEventDetailsRealtime(eventId: number): Promise<void> {
      if (this.currentAdverseEvent?.id !== eventId) return
      const epoch = currentAuthEpoch()

      if (inFlightAdverseEventById.has(eventId)) {
        await inFlightAdverseEventById.get(eventId)
        return
      }

      const requestPromise = (async () => {
        const { data } = await axios.get(`${BASE_URL}/api/adverse/adverse-event/${eventId}/`)
        if (epoch !== currentAuthEpoch() || this.currentAdverseEvent?.id !== eventId) return
        this.currentAdverseEvent.probability = data.probability
        this.currentAdverseEvent.consequence = data.consequence
        Object.assign(this.currentAdverseEvent, data, {
          probability: data.probability,
          consequence: data.consequence,
          date_time: new Date(data.date_time),
        })
        this.editableAdverseEvent = JSON.parse(JSON.stringify(this.currentAdverseEvent))
        this.externalAdverseEventId = null
      })()

      inFlightAdverseEventById.set(eventId, requestPromise)
      try {
        await requestPromise
      } finally {
        inFlightAdverseEventById.delete(eventId)
      }

      await this.fetchAdverseEventLogs(eventId)
    },

    /**
     * Обработка realtime-событий записей ответственности (responsibility_entry_*) для открытого НС.
     * Payload Centrifuge не пишем: в нём actions пустой. Карточку заново берём с GET.
     */
    async applyResponsibilityEntryRealtime(payload: IRealtimePayload | any): Promise<void> {
      if (!payload?.event_type || !payload?.data) return
      const entry = payload.data as any
      if (entry.source_type !== 'adverseevent') return

      const eventId = Number(entry.url_props?.eventId)
      if (!eventId || this.currentAdverseEvent?.id !== eventId) return

      if (realtimeRefreshTimer) {
        clearTimeout(realtimeRefreshTimer)
      }
      realtimeRefreshTimer = setTimeout(async () => {
        realtimeRefreshTimer = null
        await this.refreshAdverseEventDetailsRealtime(eventId)
      }, 150)
    },

    // POST
    /**
     * Создает новое нежелательное событие (НС) через API
     *
     * Последовательность действий:
     * 1. Активирует глобальный индикатор загрузки
     * 2. Отправляет данные на сервер через POST-запрос
     * 3. При успехе:
     *    - Добавляет созданное событие в начало списка
     *    - Показывает уведомление об успехе
     *    - Сбрасывает связанные мероприятия
     * 4. При ошибке:
     *    - Показывает уведомление с ошибкой
     *    - Пробрасывает исключение дальше
     * 5. В любом случае выключает индикатор загрузки
     *
     * @throws {Error} В случае ошибки API или сети
     */
    async createAdverseEvent(payload: ICreateAdversePayload, equipment?: { id: string; command: string }) {
      const feedbackStore = useFeedbackStore()

      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post<IAdverseEvent>(
          equipment ? `${BASE_URL}/api/equipment/devices/${equipment.id}/create-related/` : `${BASE_URL}/api/adverse/adverse-event/`,
          equipment ? { kind: 'adverse_event', command_id: equipment.command, payload } : payload,
        )

        this.adverseEvents.unshift(response.data)
        feedbackStore.showToast({
          type: 'success',
          message: 'Нежелательное событие успешно создано!',
          time: 7000,
        })

        return response.data
      } catch (error) {
        if (isAxiosError(error)) {
          feedbackStore.showToast({
            type: 'error',
            message: getApiError(error, 'Произошла неизвестная ошибка'),
            time: 7000,
          })
        }

        logger.error('createAdverseEvent_error', {
          store: 'apiStore',
          function: 'createAdverseEvent',
          error: `❌ Ошибка создания НС: ${error}`,
          payload,
        })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Берёт нежелательное событие (НС) в работу через API
     *
     * Последовательность действий:
     * 1. Активирует глобальный индикатор загрузки
     * 2. Отправляет POST-запрос на сервер для смены статуса НС на "in-progress"
     * 3. При успехе:
     *    - Перезапрашивает данные НС по ID
     *    - Показывает уведомление об успехе
     * 4. При ошибке:
     *    - Логирует ошибку
     *    - Показывает уведомление с ошибкой
     *    - Пробрасывает исключение дальше
     * 5. В любом случае выключает индикатор загрузки
     *
     * @param payload Объект с ID НС
     * @throws {Error} В случае ошибки API или сети
     */
    async takeAdverseEvent(payload: { id: number; entry_id: number }): Promise<void> {
      const feedbackStore = useFeedbackStore()

      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post(
          `${BASE_URL}/api/adverse/adverse-event/${payload.id}/responsibility-entries/${payload.entry_id}/in-progress/`,
        )

        const updatedCoordinatorEntry = response.data

        // Обновляем только coordinator_entry в текущем событии
        if (this.currentAdverseEvent?.id === payload.id) {
          this.currentAdverseEvent.coordinator_entry = updatedCoordinatorEntry
        }

        // Обновляем только coordinator_entry в редактируемой копии
        if (this.editableAdverseEvent?.id === payload.id) {
          this.editableAdverseEvent.coordinator_entry = updatedCoordinatorEntry
        }
      } catch (error) {
        logger.error('takeAdverseEvent_error', {
          store: 'apiStore',
          function: 'takeAdverseEvent',
          error: `❌ Ошибка взятия НС в работу: ${error}`,
          payload,
        })
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось взять НС в работу'),
          time: 7000,
        })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async returnAdverseEventAtWork(payload: { id: number; entry_id: number }): Promise<void> {
      const feedbackStore = useFeedbackStore()

      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post(
          `${BASE_URL}/api/adverse/adverse-event/${payload.id}/responsibility-entries/${payload.entry_id}/return/`,
        )

        const updatedEvent = response.data

        // Обновляем только coordinator_entry в текущем событии
        if (this.currentAdverseEvent?.id === payload.id) {
          this.currentAdverseEvent.coordinator_entry = updatedEvent
        }

        // Обновляем только coordinator_entry в редактируемой копии
        if (this.editableAdverseEvent?.id === payload.id) {
          this.editableAdverseEvent.coordinator_entry = updatedEvent
        }

        feedbackStore.showToast({
          type: 'success',
          message: 'НС возвращено в работу',
          time: 4000,
        })
      } catch (error) {
        logger.error('returnAdverseEventAtWork_error', {
          store: 'apiStore',
          function: 'returnAdverseEventAtWork',
          error: `❌ Ошибка возврата НС в работу: ${error}`,
          payload,
        })
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось вернуть НС в работу'),
          time: 7000,
        })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Завершает нежелательное событие (НС) через API
     *
     * Последовательность действий:
     * 1. Активирует глобальный индикатор загрузки
     * 2. Отправляет POST-запрос на сервер для смены статуса НС на "complete"
     * 3. При успехе:
     *    - Перезапрашивает данные НС по ID
     *    - Показывает уведомление об успехе
     * 4. При ошибке:
     *    - Логирует ошибку
     *    - Показывает уведомление с ошибкой
     *    - Пробрасывает исключение дальше
     * 5. В любом случае выключает индикатор загрузки
     *
     * @param payload Объект с ID НС
     * @throws {Error} В случае ошибки API или сети
     */
    async completeAdverseEvent(payload: {
      id: number
      entry_id: number
      report?: string
      files?: File[]
    }): Promise<void> {
      const feedbackStore = useFeedbackStore()

      feedbackStore.isGlobalLoading = true

      try {
        const formData = new FormData()

        // Добавляем отчет если есть
        if (payload.report) {
          formData.append('report', payload.report)
        }

        // Добавляем файлы если есть
        if (payload.files && payload.files.length > 0) {
          payload.files.forEach(file => {
            formData.append('files', file)
          })
        }

        const response = await axios.post(
          `${BASE_URL}/api/adverse/adverse-event/${payload.id}/responsibility-entries/${payload.entry_id}/complete/`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        )

        const updatedEvent = response.data

        // Обновляем только coordinator_entry в текущем событии
        if (this.currentAdverseEvent?.id === payload.id) {
          this.currentAdverseEvent.coordinator_entry = updatedEvent
        }

        // Обновляем только coordinator_entry в редактируемой копии
        if (this.editableAdverseEvent?.id === payload.id) {
          this.editableAdverseEvent.coordinator_entry = updatedEvent
        }

        feedbackStore.showToast({
          type: 'success',
          message: 'НС успешно завершено',
          time: 4000,
        })
      } catch (error) {
        logger.error('completeAdverseEvent_error', {
          store: 'apiStore',
          function: 'completeAdverseEvent',
          error: `❌ Ошибка при завершении НС: ${error}`,
          payload,
        })
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось завершить НС'),
          time: 7000,
        })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    // GET

    /**
     * Загружает отфильтрованные нежелательные события с сервера
     *
     * Логика работы:
     * 1. Параметры первого запроса (без cursor):
     *    - categories, coordinators, departments, event_types, numbers, risk, status
     *    - search
     *    - created_after / created_before (в API-формате)
     *    - is_active
     *    - page_size (itemsPerPage)
     * 2. Для последующих запросов использует только cursor пагинации
     * 3. Обновляет стор:
     *    - this.adverseEvents (добавляет новые события при наличии курсора, заменяет при первом запросе)
     *    - this.totalAdverseEventsCount
     *    - this.adverseEventsStats
     *    - this.nextAdverseEventsCursor
     *
     * @param {string} [cursor] - Ключ пагинации для подгрузки следующей страницы
     * @param {number} [itemsPerPage] - Количество элементов на странице для первого запроса или при смене itemsPerPage
     * @throws {Error} При ошибке сетевого запроса или API
     */
    async fetchFilteredAdverseEvents(cursor?: string, itemsPerPage?: number, throwOnError = false): Promise<void> {
      const feedbackStore = useFeedbackStore()

      if (this.adverseEvents.length === 0) {
        feedbackStore.isGlobalLoading = true
      }

      const revision = ++this.adverseEventsRequestRevision
      this.adverseSearchError = ''
      try {
        // Собираем параметры из фильтров (только для первого запроса)
        const params: Record<string, string | number | boolean | undefined> = {}

        // Приоритет: переданный аргумент sort -> значение из state -> дефолт
        if (this.currentSortString) {
          params.sort = this.currentSortString
        }

        if (itemsPerPage) {
          this.itemsPerPage = itemsPerPage
        }

        if (this.itemsPerPage) {
          params.page_size = this.itemsPerPage
        }

        Object.assign(params, adverseReportFilters(this.filters))

        // Если это запрос следующей страницы — только cursor
        if (cursor) {
          params.cursor = cursor
        }

        const response = await axios.get(`${BASE_URL}/api/adverse/adverse-event/`, { params })

        if (revision !== this.adverseEventsRequestRevision) return
        if (cursor) {
          this.adverseEvents = [...this.adverseEvents, ...response.data.results]
        } else {
          this.adverseEvents = response.data.results
          this.totalAdverseEventsCount = response.data.count
        }
        this.adverseEventsStats = response.data.stats
        this.nextAdverseEventsCursor = response.data.next

      } catch (error) {
        if (revision !== this.adverseEventsRequestRevision) return
        this.adverseSearchError = getApiError(error, 'Не удалось применить фильтр. Проверьте условия и повторите поиск.')
        if (!cursor) {
          this.adverseEvents = []
          this.totalAdverseEventsCount = 0
          this.nextAdverseEventsCursor = null
        }
        if (throwOnError) throw error
        console.error(`❌ Ошибка фильтрации НС: ${error}`)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить данные'),
          time: 7000,
        })
      } finally {
        if (revision === this.adverseEventsRequestRevision) feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает список всех отделений с сервера
     *
     * Логика работы:
     * - Устанавливает флаг загрузки перед началом запроса
     * - Сохраняет полученные отделения в хранилище (this.departments)
     * - В случае ошибки сбрасывает список отделений и показывает уведомление
     * - Гарантированно снимает флаг загрузки в finally
     *
     * Используется для инициализации списка отделений
     * при загрузке модуля работы с нежелательными событиями
     */
    async fetchAllDepartments(): Promise<void> {
      const feedbackStore = useFeedbackStore()

      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.get(`${BASE_URL}/api/employee/department/`)

        this.departments = response.data.results

        this.flatDepartments = []

        const walk = (items: any[]) => {
          items.forEach(d => {
            this.flatDepartments.push({
              id: d.id != null ? String(d.id) : '',
              full_name:
                d.short_name && d.short_name.trim() !== ''
                  ? d.short_name
                  : d.name,
            })

            if (Array.isArray(d.children) && d.children.length > 0) {
              walk(d.children)
            }
          })
        }

        walk(this.departments)

        this.departmentsTree = this.getTree(this.departments)
      } catch (error) {
        console.error(`❌ Ошибка получения отделений: ${error}`)
        this.departments = []
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Ошибка получения списка отделений'),
          time: 7000,
        })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает дерево активных сотрудников (GET /api/employee/employees/tree/?is_active=true)
     *
     * Основная функциональность:
     * - Управляет состоянием загрузки через feedbackStore
     * - Сохраняет дерево (отделения → сотрудники) без преобразований
     * - В случае ошибки показывает уведомление
     * - Гарантированно снимает флаг загрузки после выполнения
     *
     * Реактивность:
     * - Дерево сохраняется через markRaw: реактивна только замена массива целиком,
     *   узлы не оборачиваются в Proxy
     * - Причина: дерево большое, а глубокая реактивность заставляла Vue DevTools
     *   сериализовать его на каждом обновлении компонента, и вкладка падала по памяти
     * - Ограничение: узлы дерева нельзя мутировать точечно — только заменять массив целиком
     *
     * Используется в модалках выбора сотрудников и формах (НС, аудиты, заявки, ППУ и др.)
     */
    async fetchAllEmployees(options?: { silent?: boolean }): Promise<void> {
      const feedbackStore = useFeedbackStore()

      if (!options?.silent)
        feedbackStore.isGlobalLoading = true

      try {
        const response = await axios.get(`${BASE_URL}/api/employee/employees/tree/`, {
          params: { is_active: true },
        })

        this.employees = markRaw(response.data.results)
      } catch (error) {
        console.error(`❌ Ошибка получения сотрудников: ${error}`)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить список сотрудников'),
          time: 7000,
        })
      } finally {
        if (!options?.silent)
          feedbackStore.isGlobalLoading = false
      }
    },

    async fetchEmployeeById(id: string) {
      const feedbackStore = useFeedbackStore()

      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.get<ICurrentEmployee>(`${BASE_URL}/api/employee/employees/${id}/`)

        return response.data
      } catch (error) {
        console.error(`❌ Ошибка получения сотрудника: ${error}`)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить сотрудника'),
          time: 7000,
        })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает структуру больницы (корпуса, этажи, помещения)
     *
     * Ключевые особенности:
     * - Получает и сохраняет полную иерархию помещений (госпитальная структура)
     * - Сохраняет только первый элемент из ответа сервера (results[0])
     * - Возвращает полные данные ответа API
     * - При ошибке показывает уведомление и пробрасывает исключение
     *
     * Используется для работы с локациями при создании/редактировании событий
     * и выборе места происшествия
     */
    async fetchHospitalSkeleton() {
      const feedbackStore = useFeedbackStore()

      feedbackStore.isGlobalLoading = true

      try {
        const response = await axios.get(
          `${BASE_URL}/api/hospital/hospital/`,
        )

        this.hospitalSkeleton = response.data.results

        this.hospitalTree = this.getTree(this.hospitalSkeleton)

        return response.data
      } catch (error) {
        console.error(`❌ Ошибка получения данных больницы: ${error}`)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить данные больницы'),
          time: 7000,
        })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает детальную информацию о нежелательном событии по ID
     *
     * Основные функции:
     * - Получает полные данные события с сервера
     * - Сохраняет в currentAdverseEvent (для формы редактирования)
     * - Создает контрольную копию в editableAdverseEvent для сравнения изменений
     * - Преобразует строковые даты в Date объекты:
     *   - Основная дата события
     *   - Сроки выполнения мероприятий
     *   - Даты создания/обновления записей
     *
     * Особенности:
     * - Управляет состоянием загрузки через feedbackStore
     * - Обрабатывает и пробрасывает ошибки API
     * - Используется при открытии формы редактирования события
     */
    async fetchAdverseEventById(payload: { id: number }): Promise<void> {
      const eventId = payload.id
      if (inFlightAdverseEventById.has(eventId)) {
        return inFlightAdverseEventById.get(eventId)!
      }

      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      const requestPromise = (async () => {
        try {
          const response = await axios.get(`${BASE_URL}/api/adverse/adverse-event/${eventId}/`)
          const result = response.data

          this.currentAdverseEvent.probability = result.probability
          this.currentAdverseEvent.consequence = result.consequence
          Object.assign(this.currentAdverseEvent, result)
          this.currentAdverseEvent.probability = result.probability
          this.currentAdverseEvent.consequence = result.consequence
          this.currentAdverseEvent.date_time = new Date(result.date_time)
          this.editableAdverseEvent = JSON.parse(JSON.stringify(this.currentAdverseEvent))
        } catch (error) {
          console.error(`❌ Ошибка получения НС по ID: ${error}`)
          feedbackStore.showToast({
            type: 'error',
            message: getApiError(error, 'Не удалось получить нежелательное событие'),
            time: 7000,
          })
          if (isHttpNotFound(error))
            throw error
        } finally {
          feedbackStore.isGlobalLoading = false
        }
      })()

      inFlightAdverseEventById.set(eventId, requestPromise)
      try {
        await requestPromise
      } finally {
        inFlightAdverseEventById.delete(eventId)
      }
    },

    /**
     * Загружает историю изменений для указанного НС
     * @param eventId - ID события
     */
    async fetchAdverseEventLogs(eventId: number): Promise<IHistoryLog[]> {
      if (!usePermissionsStore().canViewChangeHistory) {
        this.adverseEventLogs = []
        return []
      }
      if (inFlightAdverseEventLogs.has(eventId)) {
        return inFlightAdverseEventLogs.get(eventId)!
      }

      const requestPromise = (async () => {
        try {
          const response = await axios.get(onboardingEntityApiUrl('adverse_event', eventId, `/adverse/adverse-event/logs/?event_id=${eventId}`))
          let logs: IHistoryLog[] = []
          if (Array.isArray(response.data)) {
            logs = response.data
          } else if (response.data && Array.isArray(response.data.results)) {
            logs = response.data.results
          }
          this.adverseEventLogs = logs
          return logs
        } catch (error) {
          console.error(`❌ Ошибка загрузки логов НС: ${error}`)
          return []
        }
      })()

      inFlightAdverseEventLogs.set(eventId, requestPromise)
      try {
        return await requestPromise
      } finally {
        inFlightAdverseEventLogs.delete(eventId)
      }
    },

    /**
     
     */
    async fetchProbabilityAndConsequence(): Promise<void> {
      const feedbackStore = useFeedbackStore()

      feedbackStore.isGlobalLoading = true

      try {
        const response = await axios.get<IProbabilityConsequenceResponse>(
          `${BASE_URL}/api/adverse/probability-consequence/`,
        )

        // Явно указываем тип для массивов
        const probabilityOptions: ISelectOption[] = response.data.probability.map(([value, label]) => ({
          label,
          value,
          disabled: false,
        }))

        const consequenceOptions: ISelectOption[] = response.data.consequence.map(([value, label]) => ({
          label,
          value,
          disabled: false,
        }))

        this.adverseEventProbability = probabilityOptions
        this.adverseEventConsequences = consequenceOptions
      } catch (error) {
        console.error('Ошибка загрузки опций вероятности и последствий:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Произошла ошибка при загрузке опций'),
          time: 7000,
        })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    // PATCH
    /**
     * Обновляет данные нежелательного события через API
     *
     * Основные функции:
     * - Отправляет PATCH-запрос с обновленными данными события
     * - Обновляет данные в локальном хранилище после успешного ответа сервера
     * - Показывает уведомления о результате операции
     * - Возвращает обновленные данные события
     *
     * Особенности:
     * - Находит и заменяет событие в массиве adverseEvents по ID
     * - Использует глобальный индикатор загрузки
     * - Пробрасывает ошибку для дальнейшей обработки
     * - Логирует процесс обновления в консоли
     *
     * Принимает {number} id - ID обновляемого события
     * Принимает {ICreateAdversePayload} payload - Обновленные данные события
     * @returns {Promise<IAdverseEvent>} Обновленные данные события
     * @throws {Error} В случае ошибки обновления
     */
    async updateAdverseEvent({ id, payload }: { id: number; payload: ICreateAdversePayload }): Promise<void> {
      const feedbackStore = useFeedbackStore()

      feedbackStore.isGlobalLoading = true
      try {
        // // PATCH-запрос на обновление события
        const response = await axios.patch(
          `${BASE_URL}/api/adverse/adverse-event/${id}/`,
          payload,
        )

        // // Находим индекс обновляемого события
        const index = this.adverseEvents.findIndex(item => item.id === id)
        if (index !== -1) {
          this.currentAdverseEvent = response.data
          this.editableAdverseEvent = JSON.parse(JSON.stringify(this.currentAdverseEvent))
        }

        feedbackStore.showToast({
          type: 'success',
          message: 'НС успешно обновлено',
          time: 4000,
        })

        return response.data
      } catch (error) {
        logger.error('updateAdverseEvent_error', {
          store: 'apiStore',
          function: 'updateAdverseEvent',
          error: `❌ Ошибка обновления НС: ${error}`,
          payload,
        })
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось обновить НС'),
          time: 7000,
        })
        throw error
      } finally {
        // // Отключаем индикатор загрузки
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * PATCH только вероятности и последствий.
     * После успеха обновляет эти поля (и пересчитанный risk) в current
     * и в эталоне editable — чтобы checkFormChanges не видел автосейв как грязь,
     * но сохранял несохранённые правки вкладки «Форма».
     */
    async updateAdverseEventRisk(payload: {
      id: number
      probability: number | null
      consequence: number | null
    }): Promise<void> {
      const feedbackStore = useFeedbackStore()

      try {
        const response = await axios.patch(
          `${BASE_URL}/api/adverse/adverse-event/${payload.id}/`,
          {
            probability: payload.probability,
            consequence: payload.consequence,
          },
        )

        const saved = response.data as IAdverseEventRiskFields

        if (this.currentAdverseEvent?.id === payload.id)
          applyAdverseEventRiskFields(this.currentAdverseEvent, saved)

        if (this.editableAdverseEvent?.id === payload.id)
          applyAdverseEventRiskFields(this.editableAdverseEvent, saved)

        const index = this.adverseEvents.findIndex(item => item.id === payload.id)
        if (index !== -1)
          applyAdverseEventRiskFields(this.adverseEvents[index], saved)
      } catch (error) {
        logger.error('updateAdverseEventRisk_error', {
          store: 'apiStore',
          function: 'updateAdverseEventRisk',
          error: `❌ Ошибка обновления оценки риска: ${error}`,
          payload,
        })
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось обновить вероятность или последствия'),
          time: 7000,
        })
        throw error
      }
    },

    /**
     * Изменяет координатора для указанного нежелательного события
     *
     * Логика работы:
     * - Отправляет POST-запрос на сервер с новым `coordinator_id`
     * - При успешном обновлении (статус 200) показывает уведомление об успехе
     * - В случае ошибки логирует сообщение и отображает уведомление пользователю
     * - Управляет состоянием загрузки через feedbackStore
     *
     * Особенности:
     * - Не возвращает данные (при необходимости обновления интерфейса данные следует перезапросить)
     * - Не изменяет локальное состояние `currentAdverseEvent`
     * - Используется при выборе нового координатора через модальное окно
     */
    async changeAdverseEventCoordinator(payload: {
      event_id: number
      coordinator_id: string
    }): Promise<any> {
      const feedbackStore = useFeedbackStore()

      feedbackStore.isGlobalLoading = true

      try {
        const response = await axios.patch(
          `${BASE_URL}/api/adverse/adverse-event/${payload.event_id}/responsibility-entries/change-coordinator/`,
          { coordinator_id: payload.coordinator_id },
        )

        const updatedCoordinatorEntry = response.data

        // Обновляем в текущем событии (поле coordinator_entry)
        if (this.currentAdverseEvent?.id === payload.event_id) {
          this.currentAdverseEvent.coordinator_entry = updatedCoordinatorEntry
        }

        // Обновляем в редактируемой копии (поле coordinator_entry)
        if (this.editableAdverseEvent?.id === payload.event_id) {
          this.editableAdverseEvent.coordinator_entry = updatedCoordinatorEntry
          // console.log('--- [DEBUG STORE UPDATE] ---')
          // console.log('CoordinatorEntry от сервера:', updatedCoordinatorEntry)
          // console.log('Весь объект Editable после обновления:', this.editableAdverseEvent)
        }

        feedbackStore.showToast({
          type: 'success',
          message: 'Координатор успешно изменён',
          time: 7000,
        })
        return updatedCoordinatorEntry
      } catch (error) {
        logger.error('changeAdverseEventCoordinator_error', {
          store: 'apiStore',
          function: 'changeAdverseEventCoordinator',
          error: `❌ Ошибка при изменении координатора: ${error}`,
          payload,
        })
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось изменить координатора'),
          time: 7000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async checkBackendStatus() {
      try {
        const response = await axios.get(
          `${BASE_URL}/system/status/`,
          {
            skipAuth: true,
          } as ICustomAxiosRequestConfig,
        )

        return response.status === 200
      } catch (error) {
        return false
      }
    },

    // WITHOUT AXIOS

    /**
     * Сбрасывает все фильтры нежелательных событий к значениям по умолчанию
     *
     * Восстанавливает начальное состояние фильтров:
     * - Очищает массивы выбранных категорий, координаторов, подразделений, видов событий, номеров, рисков и статусов
     * - Сбрасывает текстовый поиск
     * - Обнуляет фильтры по датам и активности
     * - Сбрасывает курсор пагинации
     *
     * Используется:
     * - При ручном сбросе фильтров пользователем
     * - При переходе между разделами приложения
     * - Для очистки формы фильтрации
     */
    // Переключатель «Скрыть завершённые» (is_actual) живёт отдельно от условий поиска и сбросом не трогается
    resetFilters() {
      this.filters = {
        categories: [],
        coordinators: [],
        creators: [],
        created_after: null,
        created_before: null,
        cursor: null,
        departments: [],
        event_types: [],
        is_active: null,
        requires_action: null,
        is_actual: this.filters.is_actual ?? null,
        numbers: [],
        risk: [],
        search: '',
        status: [],
      }
    },

    /**
     * Сбрасывает текущее нежелательное событие до начального состояния
     *
     * Восстанавливает форму создания/редактирования события:
     * - Очищает все поля описания, местоположения и параметров события
     * - Устанавливает тип события в значение по умолчанию (id = 0)
     * - Сбрасывает статус на `'new'`
     * - Обнуляет вероятности, последствия, время и участников
     * - Очищает исполнителей и связанные записи
     * - Удаляет ссылку на редактируемое событие
     *
     * Используется:
     * - При создании нового нежелательного события
     * - При отмене редактирования существующего события
     * - После успешного сохранения или удаления события
     */
    resetCurrentAdverseEvent() {
      this.externalAdverseEventId = null
      this.currentAdverseEvent = {
        event_type: { id: '' },
        location: null,
        location_str: '',
        department: null,
        description: '',
        status: 'new',
        probability: null,
        consequence: null,
        date_time: '',
        participants: [],
      } as ICreateAdversePayload
      this.editableAdverseEvent = null
    },

    /**
     * Загрузка вариантов для выпадающих списков фильтрации (UniversalFilterTree).
     * UPD: Добавлена отправка активных фильтров (контекст) и замена search на search_field
     */
    async fetchFilterChoices(fieldKey: string, search = '', cursor?: string, module?: IUniversalTreeSelectModule) {
      if (!fieldKey) return

      // Устанавливаем флаг загрузки для конкретного поля
      this.filterLoading[fieldKey] = true

      try {
        const params: any = {
          field: fieldKey === 'risk' ? 'risk_level' : fieldKey,
        }

        // 1. Изменение параметра поиска
        if (search) params.search_field = search

        if (cursor) params.cursor = cursor

        // 2. Подмешивание активных фильтров
        const filters = this.filters as IFilters

        // Отделения
        if (fieldKey !== 'departments' && filters.departments.length) {
          params.departments = filters.departments.join(',')
        }

        // Типы событий
        if (fieldKey !== 'event_types' && filters.event_types.length) {
          params.event_types = filters.event_types.join(',')
        }

        // Категории
        if (fieldKey !== 'categories' && filters.categories.length) {
          params.categories = filters.categories.join(',')
        }

        // Координаторы
        if (fieldKey !== 'coordinators' && filters.coordinators.length) {
          params.coordinators = filters.coordinators.join(',')
        }

        // Регистраторы (creators)
        if (fieldKey !== 'creators' && filters.creators.length) {
          params.creators = filters.creators.join(',')
        }

        // Номера
        if (fieldKey !== 'numbers' && filters.numbers.length) {
          params.numbers = filters.numbers.join(',')
        }

        // Риск
        if (fieldKey !== 'risk' && filters.risk.length) {
          params.risk_level = filters.risk.join(',')
        }

        // Статус
        if (fieldKey !== 'status' && filters.status.length) {
          params.status = filters.status.join(',')
        }

        // Активные (мои)
        if (fieldKey !== 'is_active' && filters.is_active !== null) {
          params.is_active = filters.is_active
        }

        // Даты
        if (fieldKey !== 'created_after' && filters.created_after) {
          params.created_after = toApiDate(filters.created_after)
        }
        if (fieldKey !== 'created_before' && filters.created_before) {
          params.created_before = toApiDate(filters.created_before)
        }

        // Общий поиск
        if (filters.search) {
          params.search = filters.search
        }

        // Запрос к API списка вариантов фильтрации
        const response = await axios.get(`${BASE_URL}/api/adverse/adverse-event/filter-choices/`, { params })

        // Деструктуризация: достаем strategy
        const { results, next, strategy } = response.data

        // Сохраняем стратегию для данного поля в стейт (если null/undefined -> 'independent')
        this.filterStrategies[fieldKey] = strategy || 'independent'

        if (search) {
          // Логика для режима поиска
          if (cursor) {
            // Догрузка результатов поиска
            this.searchChoices[fieldKey] = [...(this.searchChoices[fieldKey] || []), ...results]
          } else {
            // Новый поиск - перезапись
            this.searchChoices[fieldKey] = results
          }
        } else {
          // Логика для обычного списка (открытие меню)
          if (cursor) {
            // Бесконечная прокрутка
            this.filterChoices[fieldKey] = [...(this.filterChoices[fieldKey] || []), ...results]
          } else {
            // Первая загрузка
            this.filterChoices[fieldKey] = results
          }
        }

        // Сохраняем курсор следующей страницы для этого поля
        this.filterCursors[fieldKey] = next
      } catch (e) {
        console.error(`Ошибка загрузки вариантов фильтра [${fieldKey}]:`, e)
      } finally {
        this.filterLoading[fieldKey] = false
      }
    },

    updateRequiresAction(id: number, requiresAction: boolean) {
      const event = this.adverseEvents.find(e => Number(e.id) === id)
      if (event) {
        event.requires_action = requiresAction
      }
    },
  },
})
